import Vue from 'vue';
import GoalsAPI from '../../api/goals';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: [],
  // Evaluations are kept apart from the goals themselves: they are derived from
  // a query and change on every read, while the goal definition does not.
  evaluations: {},
  // Metas cuja avaliação FALHOU (fonte indisponível, consulta recusada ou com
  // erro), por id: `{ code, message }`. Sem isto a falha chegava como `null`,
  // igual a "sem dados no período", e a tela não tinha como separar os dois.
  evaluationFailures: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isEvaluating: false,
    // 403 na listagem = a conta não tem o módulo (portão da #1398).
    isModuleUnavailable: false,
    // A listagem falhou por outro motivo: a tela mostra erro, não "nenhuma meta".
    hasFetchError: false,
    // `evaluate_all` inteiro falhou: nenhuma meta tem número, e isso é erro,
    // não "sem dados no período".
    hasEvaluationError: false,
  },
};

export const getters = {
  getGoals: $state => $state.records,
  getEvaluations: $state => $state.evaluations,
  getEvaluation: $state => id => $state.evaluations[id],
  getEvaluationFailures: $state => $state.evaluationFailures,
  getUIFlags: $state => $state.uiFlags,
};

export const actions = {
  get: async ({ commit }, params = {}) => {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await GoalsAPI.get(params);
      commit('SET_GOALS', response.data.goals || []);
      commit('SET_UI_FLAG', { isModuleUnavailable: false });
      commit('SET_UI_FLAG', { hasFetchError: false });
      return response.data.goals || [];
    } catch (error) {
      if (error && error.response && error.response.status === 403) {
        commit('SET_GOALS', []);
        commit('SET_UI_FLAG', { isModuleUnavailable: true });
        commit('SET_UI_FLAG', { hasFetchError: false });
        return [];
      }
      commit('SET_UI_FLAG', { hasFetchError: true });
      throwErrorMessage(error);
      return [];
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  create: async ({ commit }, payload) => {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const response = await GoalsAPI.create(payload);
      commit('ADD_GOAL', response.data.goal);
      return response.data.goal;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...payload }) => {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await GoalsAPI.update(id, payload);
      commit('EDIT_GOAL', response.data.goal);
      return response.data.goal;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  delete: async ({ commit }, id) => {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await GoalsAPI.delete(id);
      commit('DELETE_GOAL', id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  evaluateAll: async ({ commit }) => {
    // A rodada anterior não vale para esta: sem zerar, a retentativa mostrava
    // "Não foi possível calcular" no lugar de "Calculando…", e falha velha
    // sobrevivia a uma avaliação boa.
    commit('SET_EVALUATION_FAILURES', {});
    commit('SET_UI_FLAG', { isEvaluating: true, hasEvaluationError: false });
    try {
      const response = await GoalsAPI.evaluateAll();
      commit('SET_EVALUATIONS', response.data.results || {});
      // `failures` entrou depois de `results` (#1396); servidor antigo não o
      // manda, e aí nenhuma meta é tratada como falha.
      commit('SET_EVALUATION_FAILURES', response.data.failures || {});
      commit('SET_UI_FLAG', { hasEvaluationError: false });
      return response.data.results || {};
    } catch (error) {
      // A failing evaluation must not blank the list — the definitions are still
      // valid and worth showing. But it must not read as "no data" either.
      commit('SET_UI_FLAG', { hasEvaluationError: true });
      return {};
    } finally {
      commit('SET_UI_FLAG', { isEvaluating: false });
    }
  },

  evaluate: async ({ commit }, id) => {
    try {
      const response = await GoalsAPI.evaluate(id);
      commit('SET_EVALUATION', { id, evaluation: response.data.evaluation });
      return response.data.evaluation;
    } catch (error) {
      return null;
    }
  },
};

export const mutations = {
  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  SET_GOALS($state, goals) {
    $state.records = goals;
  },
  ADD_GOAL($state, goal) {
    if (!goal) return;
    $state.records = [goal, ...$state.records];
  },
  EDIT_GOAL($state, goal) {
    if (!goal) return;
    $state.records = $state.records.map(g => (g.id === goal.id ? goal : g));
  },
  DELETE_GOAL($state, id) {
    $state.records = $state.records.filter(g => g.id !== id);
    Vue.delete($state.evaluations, id);
    if ($state.evaluationFailures) Vue.delete($state.evaluationFailures, id);
  },
  SET_EVALUATIONS($state, results) {
    $state.evaluations = { ...results };
  },
  SET_EVALUATION_FAILURES($state, failures) {
    $state.evaluationFailures = { ...failures };
  },
  SET_EVALUATION($state, { id, evaluation }) {
    Vue.set($state.evaluations, id, evaluation);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
